import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type VariableCatalog = Record<string, { field: string; hint: string }[]>;

// Default {{ }} catalog (Pipedrive stage mode). The campaign builder passes an
// audience-specific catalog instead; both stay in sync with the backend
// CAMPAIGN_VARIABLES in app/services/email/templating.py.
export const CAMPAIGN_VARIABLES: VariableCatalog = {
  rep: [
    { field: "first_name", hint: "Deal owner's first name" },
    { field: "full_name", hint: "Deal owner's full name" },
    { field: "email", hint: "Deal owner's email" },
    { field: "phone_number", hint: "Deal owner's phone" },
  ],
  deal: [
    { field: "first_name", hint: "Customer first name" },
    { field: "last_name", hint: "Customer last name" },
    { field: "name", hint: "Deal name" },
    { field: "email", hint: "Customer email" },
    { field: "phone_number", hint: "Customer phone" },
    { field: "value", hint: "Deal value" },
  ],
};

type Suggestion =
  | { kind: "namespace"; ns: string; label: string; hint: string }
  | { kind: "field"; ns: string; field: string; label: string; hint: string };

/** The open `{{` token immediately before the caret, if any. */
function openToken(value: string, caret: number): { start: number; token: string } | null {
  const before = value.slice(0, caret);
  const start = before.lastIndexOf("{{");
  if (start === -1) return null;
  if (before.slice(start + 2).includes("}}")) return null; // already closed
  return { start, token: before.slice(start + 2) };
}

function suggestionsFor(token: string, catalog: VariableCatalog): Suggestion[] {
  const fieldMatch = token.match(/^\s*([a-zA-Z_]+)\.([a-zA-Z_]*)$/);
  if (fieldMatch) {
    const [, ns, partial] = fieldMatch;
    return (catalog[ns] ?? [])
      .filter((f) => f.field.startsWith(partial))
      .map((f) => ({ kind: "field", ns, field: f.field, label: `${ns}.${f.field}`, hint: f.hint }));
  }
  const partialNs = token.replace(/^\s*/, "");
  if (/^[a-zA-Z_]*$/.test(partialNs)) {
    return Object.keys(catalog)
      .filter((ns) => ns.startsWith(partialNs))
      .map((ns) => ({ kind: "namespace", ns, label: ns, hint: `${ns}.…` }));
  }
  return [];
}

interface VariableFieldProps {
  as: "input" | "textarea";
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Autocomplete is only offered when true (Deal-stage / audience mode). */
  enabled: boolean;
  /** Variable namespaces to suggest. Defaults to the Pipedrive stage catalog. */
  catalog?: VariableCatalog;
  placeholder?: string;
  maxLength?: number;
  /** Textarea only. */
  rows?: number;
  ariaLabel?: string;
}

/**
 * Subject/body field with inline `{{ }}` variable autocomplete. Typing `{{`
 * suggests namespaces (rep, deal); `{{ deal.` suggests that namespace's fields.
 * Selecting a namespace inserts `{{ ns.` and keeps suggesting; selecting a field
 * completes `{{ ns.field }}`.
 */
export function VariableField({
  as,
  id,
  value,
  onChange,
  className,
  enabled,
  catalog = CAMPAIGN_VARIABLES,
  placeholder,
  maxLength,
  rows,
  ariaLabel,
}: VariableFieldProps) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [active, setActive] = useState(0);
  const [tokenStart, setTokenStart] = useState<number | null>(null);
  const pendingCaret = useRef<number | null>(null);

  const refresh = (nextValue: string, caret: number) => {
    if (!enabled) return;
    const ctx = openToken(nextValue, caret);
    if (!ctx) {
      setSuggestions([]);
      setTokenStart(null);
      return;
    }
    const next = suggestionsFor(ctx.token, catalog);
    setSuggestions(next);
    setTokenStart(next.length ? ctx.start : null);
    setActive(0);
  };

  // Restore the caret after a programmatic value change (insertion).
  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      ref.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      refresh(value, pendingCaret.current);
      pendingCaret.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      setTokenStart(null);
    }
  }, [enabled]);

  const apply = (s: Suggestion) => {
    if (tokenStart == null || !ref.current) return;
    const caret = ref.current.selectionStart ?? value.length;
    const head = value.slice(0, tokenStart);
    const tail = value.slice(caret);
    const inserted = s.kind === "namespace" ? `{{ ${s.ns}.` : `{{ ${s.ns}.${s.field} }}`;
    // A pick that would run past the limit is left out, as typing past it would be.
    if (maxLength != null && (head + inserted + tail).length > maxLength) return;
    pendingCaret.current = (head + inserted).length;
    onChange(head + inserted + tail);
    if (s.kind === "field") {
      setSuggestions([]);
      setTokenStart(null);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      apply(suggestions[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setSuggestions([]);
      setTokenStart(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(e.target.value);
    refresh(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const open = suggestions.length > 0 && tokenStart != null;
  const shared = {
    id,
    value,
    onChange: handleChange,
    onKeyDown,
    onBlur: () => window.setTimeout(() => setSuggestions([]), 120),
    className,
    placeholder,
    maxLength,
    "aria-label": ariaLabel,
  };

  return (
    <div className="relative">
      {as === "textarea" ? (
        <Textarea ref={ref as React.Ref<HTMLTextAreaElement>} rows={rows} {...shared} />
      ) : (
        <Input ref={ref as React.Ref<HTMLInputElement>} {...shared} />
      )}
      {open ? (
        <ul className="absolute z-50 mt-1 max-h-56 w-72 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {suggestions.map((s, i) => (
            <li key={s.label}>
              <button
                type="button"
                // onMouseDown (not onClick) so it fires before the field's blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  apply(s);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                  i === active ? "bg-accent" : "hover:bg-accent",
                )}
              >
                <span className="font-mono text-xs">{s.label}</span>
                <span className="truncate text-xs text-muted-foreground">{s.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
